/* App shell: routing, theme, nav, contact band and footer. */

import React from 'react';
import ReactDOM from 'react-dom/client';
import './site.css';
import { PRODUCTS, INDUSTRIES, SIGN_IN_URL, ACCENT } from './data.js';
import { parseRoute, routePath } from './routes.js';
import { applyMeta } from './seo.js';
import { NavContext, Link, Icon, scrollToId, openDemoBooking } from './ui.jsx';
import { HomePage } from './page-home.jsx';
import { ProductPage } from './page-product.jsx';
import { IndustryPage } from './page-industry.jsx';
import { AboutPage } from './page-about.jsx';

function toggleTheme() {
  const root = document.documentElement;
  const next = root.dataset.theme === 'day' ? 'night' : 'day';
  root.dataset.theme = next;
  try { localStorage.setItem('evx-theme', next); } catch (e) { /* storage blocked */ }
}

function App() {
  const [route, setRoute] = React.useState(() => parseRoute(window.location.pathname));
  const [menu, setMenu] = React.useState(null); // 'products' | 'solutions' | 'mobile' | null

  // Navigate to "/path" or "/path#section". Same-page hashes just scroll.
  const navigate = React.useCallback((to) => {
    const [path, hash] = to.split('#');
    const next = parseRoute(path || '/');
    setMenu(null);
    if (routePath(next) !== window.location.pathname) {
      window.history.pushState(null, '', routePath(next) + (hash ? '#' + hash : ''));
      setRoute(next);
      if (hash) setTimeout(() => scrollToId(hash), 50);
      else window.scrollTo(0, 0);
    } else if (hash) {
      scrollToId(hash);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  React.useEffect(() => {
    const onPop = () => { setRoute(parseRoute(window.location.pathname)); setMenu(null); };
    const onKey = (e) => { if (e.key === 'Escape') setMenu(null); };
    window.addEventListener('popstate', onPop);
    window.addEventListener('keydown', onKey);
    // Deep link with a fragment (e.g. /#roi): scroll once layout settles.
    if (window.location.hash.length > 1) setTimeout(() => scrollToId(window.location.hash.slice(1)), 100);
    return () => { window.removeEventListener('popstate', onPop); window.removeEventListener('keydown', onKey); };
  }, []);

  React.useEffect(() => { applyMeta(route); }, [route]);

  const page = route.page === 'product' ? <ProductPage productKey={route.key} />
    : route.page === 'industry' ? <IndustryPage industryKey={route.key} />
    : route.page === 'about' ? <AboutPage />
    : <HomePage />;

  return (
    <NavContext.Provider value={{ navigate }}>
      <a href="#main" className="skip-link">Skip to main content</a>
      <Nav route={route} menu={menu} setMenu={setMenu} />
      {menu && <div className="scrim" onClick={() => setMenu(null)} />}
      <div className="nav-spacer" />
      <main id="main" key={routePath(route)}>{page}</main>
      <Contact />
      <Footer />
    </NavContext.Provider>
  );
}

function Nav({ route, menu, setMenu }) {
  const toggle = (k) => () => setMenu(menu === k ? null : k);
  const current = (page) => (route.page === page && !menu ? 'page' : undefined);
  return (
    <header className="nav">
      <div className="wrap nav-bar">
        <Link to="/" className="nav-brand" aria-label="EvoNexon home">
          <img src="/assets/live-eye.svg" alt="" width="28" height="28" />
          <span>EvoNexon</span>
        </Link>
        <nav className="nav-links" aria-label="Main">
          <Link to="/" className="nav-link hide-md" aria-current={current('home')}>Home</Link>
          <button type="button" className="nav-link hide-md" aria-expanded={menu === 'products'} onClick={toggle('products')}
                  style={route.page === 'product' ? { color: 'var(--ac)' } : undefined}>
            Products<span className="nav-caret" aria-hidden="true">▼</span>
          </button>
          <button type="button" className="nav-link hide-md" aria-expanded={menu === 'solutions'} onClick={toggle('solutions')}
                  style={route.page === 'industry' ? { color: 'var(--ac)' } : undefined}>
            Solutions<span className="nav-caret" aria-hidden="true">▼</span>
          </button>
          <Link to="/about" className="nav-link hide-md" aria-current={current('about')}>About</Link>
          <ThemeToggle className="nav-pill hide-sm" />
          <a href={SIGN_IN_URL} className="nav-signin hide-md" target="_blank" rel="noopener">Sign in</a>
          <a href="#contact" className="btn btn-primary btn-sm hide-sm" onClick={(e) => { e.preventDefault(); setMenu(null); scrollToId('contact'); }}>
            <Icon name="event" /><span>Talk to an expert</span>
          </a>
          <button type="button" className="nav-burger" aria-label="Menu" aria-expanded={menu === 'mobile'} onClick={toggle('mobile')}>
            <Icon name={menu === 'mobile' ? 'close' : 'menu'} />
          </button>
        </nav>
      </div>

      {menu === 'products' && (
        <div className="mega">
          <div className="wrap mega-inner">
            <div className="eyebrow mega-title">PRODUCTS · THE VIAI SYSTEM</div>
            <div className="grid g5 mega-grid">
              {PRODUCTS.map((p, i) => (
                <Link to={'/product/' + p.key} className="mega-card" key={p.key} style={{ borderTop: '3px solid ' + ACCENT[p.key] }}>
                  <div className="k">{`0${i + 1} · ${p.kind}`}</div>
                  <div className="n">{p.name}</div>
                  <div className="d">{p.short}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
      {menu === 'solutions' && (
        <div className="mega">
          <div className="wrap mega-inner">
            <div className="eyebrow mega-title">SOLUTIONS · BY INDUSTRY</div>
            <div className="grid g4 mega-grid">
              {INDUSTRIES.map(d => (
                <Link to={'/solutions/' + d.key} className="mega-card ind" key={d.key}>
                  <div className="n">{d.name}</div>
                  <div className="k">{d.defects.slice(0, 3).map(x => x.code).join(' · ')}</div>
                </Link>
              ))}
              <a href="#contact" className="mega-card ind dashed" onClick={(e) => { e.preventDefault(); setMenu(null); scrollToId('contact'); }}>
                <div className="n">Your industry</div>
                <div className="k">Send 50 sample images</div>
              </a>
            </div>
          </div>
        </div>
      )}
      {menu === 'mobile' && (
        <div className="mobile-menu">
          <Link to="/">Home</Link>
          <div className="group">PRODUCTS</div>
          {PRODUCTS.map(p => <Link to={'/product/' + p.key} key={p.key}>{p.name}</Link>)}
          <div className="group">SOLUTIONS</div>
          {INDUSTRIES.map(d => <Link to={'/solutions/' + d.key} key={d.key}>{d.name}</Link>)}
          <div className="group">COMPANY</div>
          <Link to="/about">About</Link>
          <div className="row">
            <ThemeToggle className="nav-pill" />
            <a href={SIGN_IN_URL} className="nav-signin" target="_blank" rel="noopener">Sign in</a>
            <a href="#contact" className="btn btn-primary btn-sm" onClick={(e) => { e.preventDefault(); setMenu(null); scrollToId('contact'); }}>
              <Icon name="event" /><span>Talk to an expert</span>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

// Both labels are rendered; CSS shows the one for the current theme, so the
// prerendered markup is the same whichever theme the visitor has stored.
function ThemeToggle({ className }) {
  return (
    <button type="button" className={className} onClick={toggleTheme} title="Switch day / night">
      <span className="when-night" aria-hidden="true">☀</span><span className="when-night">Day</span>
      <span className="when-day" aria-hidden="true">☾</span><span className="when-day">Night</span>
    </button>
  );
}

function Contact() {
  return (
    <section id="contact" className="contact">
      <div className="wrap grid g2 cols">
        <div>
          <h2>See it on your part.</h2>
          <p>20 minutes. Bring a sample. Leave with a working detector.</p>
        </div>
        <div className="act">
          <div className="row">
            <button type="button" className="btn btn-lg btn-white" onClick={openDemoBooking}>
              <Icon name="event" /><span>Book a 20-minute demo</span>
            </button>
            <a href="mailto:sales@evonexon.com" className="btn btn-lg btn-line">sales@evonexon.com</a>
          </div>
          <div className="note">Plants in Canada and the US. Reply within one business day.</div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="wrap top">
        <div className="brand">
          <div><img src="/assets/live-eye.svg" alt="" width="24" height="24" /><span>EvoNexon</span></div>
          <div>Industrial AI for visual inspection, from the line to the management dashboard.</div>
        </div>
        <div className="cols">
          <div className="col"><div className="k">PRODUCTS</div>
            {PRODUCTS.map(p => <Link to={'/product/' + p.key} key={p.key}>{p.name}</Link>)}
          </div>
          <div className="col"><div className="k">SOLUTIONS</div>
            {INDUSTRIES.map(d => <Link to={'/solutions/' + d.key} key={d.key}>{d.name}</Link>)}
          </div>
          <div className="col"><div className="k">COMPANY</div>
            <Link to="/about">About & team</Link>
            <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToId('contact'); }}>Contact</a>
          </div>
        </div>
      </div>
      <div className="wrap bottom">
        <div>© 2024–2026 EvoNexus Innovations Inc. · Toronto, Ontario</div>
        <div>Made in Canada · SOC 2 Type 1 in progress</div>
      </div>
    </footer>
  );
}

// Production pages ship as prerendered snapshots (scripts/prerender.mjs);
// hydrate those, and render from scratch in dev.
const root = document.getElementById('root');
if (root.firstChild) ReactDOM.hydrateRoot(root, <App />);
else ReactDOM.createRoot(root).render(<App />);
